import React, { useState, useEffect } from 'react';
import chroma from 'chroma-js';
import * as OverlayHelpers from 'components/map';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { appNotify } from 'store/actions';
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';
import { ctFields } from 'components/underwrite/shared/map/ctFields';

const CensusTractOverlay = ({ map, censusTracts, selectedCT, ctMouseoverData,
  setCTMouseoverData, setCTLegendData, mapViewType, drawIsOn, appNotify }) => {
  const [currentCTGeo, setCurrentCTGeo] = useState('');   // This keeps track of which ct summary is open instead of comparing the entire object
  const [lastCTGeoFeature, setLastCTGeoFeature] = useState(null);
  const [censusTractLayer, setCensusTractLayer] = useState(new window.google.maps.Data());
  const [openEvent, setOpenEvent] = useState(null);
  const [closeEvent, setCloseEvent] = useState(null);

  useEffect(() => {
    if (censusTracts.length > 0) {
      initializeCTShapes();    // this is needed for charmNav change
    }
  },[censusTracts]);

  useEffect(() => {
    if (selectedCT === '') {
      setCTMouseoverData(null);
      setCurrentCTGeo('');
    }
    displayCTAndUpdateColors();
  },[selectedCT, censusTractLayer]);

  useEffect(() => {
    displayCTAndUpdateColors();
  },[mapViewType]);

  useEffect(() => {
    if (drawIsOn) {
      censusTractLayer.setStyle({
        fillColor: 'transparent',
        strokeColor: '#0c529e',
        strokeWeight: 1,
        opacity: 0.4,
        clickable: false
      });
    } else {
      censusTractLayer.setStyle({
        fillColor: 'transparent',
        strokeColor: '#0c529e',
        strokeWeight: 1,
        opacity: 0.4,
        clickable: true
      });
    }
  },[drawIsOn]);

  useEffect(() => {
    if (openEvent) window.google.maps.event.removeListener(openEvent);
    if (closeEvent) window.google.maps.event.removeListener(closeEvent);
    setCloseEvent(window.google.maps.event.addListener(map, 'click', closeCTSummary));     // Move closeCTSummary to when user clicks on map outside of ct layer
    setOpenEvent(window.google.maps.event.addListener(censusTractLayer, 'click', openCTSummary));
    return () => {
      if (openEvent) window.google.maps.event.removeListener(openEvent);
      if (closeEvent) window.google.maps.event.removeListener(closeEvent);
    }
  },[ctMouseoverData, currentCTGeo, lastCTGeoFeature, censusTractLayer]);

  const initializeCTShapes = () => {
    censusTractLayer.setMap(null);
    let newLayer = new window.google.maps.Data();
    setCensusTractLayer(newLayer);
    const layerArr = Object.entries(censusTracts);
    let geoJSONString = `{"type":"FeatureCollection","features":[`;
    layerArr.forEach(ct => {
      ct[1].Polygons.forEach(polygon => {
        const coordinateString = [];
        coordinateString.push(polygon.Coordinates.map((coord) => {
          return ([coord.Longitude, coord.Latitude]);
        }));
        let feature = {};
        feature.type = "Feature";
        feature.id = ct[1].Geo;
        feature.properties = { "ctData": ct[1], "stroke-color": "#0c529e", "stroke-width": 2, "stroke-opacity": 1, "fillColor": "#0c529e", "fill-opacity": 0.3, "fips": 48, "name": ct[1].GeoName };
        feature.geometry = { "type": "Polygon" };
        feature.geometry.coordinates = coordinateString;
        geoJSONString += JSON.stringify(feature) + ',';
      });
    });
    geoJSONString = geoJSONString.substring(0, geoJSONString.length - 1);
    geoJSONString += `]}`;
    const data = JSON.parse(geoJSONString);
    OverlayHelpers.createShapes(map, newLayer, { type: "geoJSON", data: data });
  };

  const displayCTAndUpdateColors = () => {
    if (selectedCT === '') {
      // clear ct shapes if user select the empty option in map CENSUS TRACT dropdown
      censusTractLayer.setMap(null);
      setCTLegendData(null);
      return;
    }

    const ctArray = calculateCTLayer(selectedCT);
    let layerData = censusTractLayer;
    let ctOpacity = 0.1;
    let strokeColor = '#0c529e';
    // if (this.props.mapViewType === 'hybrid') {
    //   ctOpacity = 0.2;
    //   strokeColor = 'yellow';
    // } else {
    //   ctOpacity = 0.1;
    //   strokeColor = '#0c529e';
    // }
    if (ctArray) {
      const colorScale = chroma.scale(["red", "yellow", "green"]).domain([ctArray.min, ctArray.max * .5, ctArray.max]).mode('lab');
      // const colorScale = chroma.scale(["red", "#FFCC00", "yellow", "green"]).domain([ctArray.min, ctArray.max * 0.25, ctArray.max * .5, ctArray.max]).mode('lab');
      // Change CT Opacity for different map view type
      layerData.forEach(function (ct) {
        const ctLayer = ctArray.property.find(function (element) {
          return ct.getId() === element.censusTract;
        });
        if (!ctLayer) {
          appNotify('Missing census tract layer data', 'warning');
          sendClientLog('Warn', 'Missing census tract layerData when running displayCTAndUpdateColors(), check to see if google map api update caused this.');
          return;
        }
        if (mapViewType === 'hybrid') {
          ctOpacity = 0.2;
          // strokeColor = colorScale(ctLayer.property).hex();
          strokeColor = 'yellow';
          // } else {
          //   ctOpacity = 0.1;
          // strokeColor = '#0c529e';
        }
        layerData.overrideStyle(ct, {
          fillColor: colorScale(ctLayer.property).hex(),
          fillOpacity: ctOpacity,
          // strokeColor: '#0c529e',
          strokeColor: strokeColor,
          strokeWeight: 1
        });
        return ct;
      });
      censusTractLayer.setMap(map);
      setCTLegendData({ title: ctArray.text, min: ctArray.min, mid: (ctArray.max * 0.5), max: ctArray.max, format: ctArray.format });
    } else {
      layerData.forEach(function (ct) {
        layerData.overrideStyle(ct, {
          fillOpacity: 0,
          // strokeColor: '#0c529e',
          strokeColor: strokeColor,
          strokeWeight: 1
        });
        return ct;
      });
    }
  }

  const openCTSummary = e => {
    // newer google map api changed this to e.feature.h
    // if (!e.feature || !e.feature.l) {
    //   //console.log(e.feature);
    //   appNotify(`Missing census tract data`, 'warning');
    //   return;
    // }

    if (e.feature && currentCTGeo === e.feature.getProperty('ctData')['Geo']) {
      if (e.feature.getProperty('isClicked')) {
        setCTMouseoverData(null);
        e.feature.setProperty('isClicked', false);
        censusTractLayer.overrideStyle(e.feature, {
          strokeWeight: 1,
          fillOpacity: 0.1
        });
      } else {
        setCTMouseoverData(e.feature.getProperty('ctData'));
        e.feature.setProperty('isClicked', true);
        censusTractLayer.overrideStyle(e.feature, {
          strokeWeight: 3,
          fillOpacity: 0.2
        })
      }
    } else {
      setCTMouseoverData(e.feature.getProperty('ctData'));
      e.feature.setProperty('isClicked', true);
      censusTractLayer.overrideStyle(e.feature, {
        strokeWeight: 3,
        fillOpacity: 0.2
      });
      if (lastCTGeoFeature && lastCTGeoFeature.getProperty('ctData')['Geo'] !== e.feature.getProperty('ctData')['Geo']) {
        lastCTGeoFeature.setProperty('isClicked', false);
        censusTractLayer.overrideStyle(lastCTGeoFeature, {
          strokeWeight: 1,
          fillOpacity: 0.1
        });
      }
      setCurrentCTGeo(e.feature.getProperty('ctData')['Geo']);
      setLastCTGeoFeature(e.feature);
    }
  }
  const closeCTSummary = (e) => {
    if (ctMouseoverData == null) {
      return;
    }
    let mouseEvent = Object.values(e).find(p => p instanceof MouseEvent);
    if (mouseEvent && mouseEvent.relatedTarget === null) { // sometimes clicking on the pins will trigger a mouseout, but the relatedTarget will be populated, so keying off that to keep the ctsummary open
      setCTMouseoverData(null);
      lastCTGeoFeature.setProperty('isClicked', false);
      censusTractLayer.overrideStyle(lastCTGeoFeature, {
        strokeWeight: 1,
        fillOpacity: 0.1
      });
      setCurrentCTGeo('');
    }
  }
  const calculateCTLayer = (ctName) => {
    switch (ctName) {
      case 'population':
        return aggregateCTData('Population', 'Population');
      case 'owner':
        return aggregateCTData('OwnerOccupiedPctSFDetached', 'Owner %', true);
      case 'rentPct':
        return aggregateCTData('RenterOccupiedPctSFDetached', 'Rent %', true);
      case 'vacant':
        return aggregateCTData('VacantPctSFDetached', 'Vacant %', true);
      case 'rent':
        return aggregateCTData('MedianRent', 'Rent Price ($)');
      case 'medianHomeValue':
        return aggregateCTData('MedianAvmValue', 'Median Home Value ($)');
      case 'crime':
        return aggregateCTData('CrimeIndex', 'Crime Index', true);
      case 'wealth':
        return aggregateCTData('Wealth', 'Wealth (%)');
      case 'hpa6mo':
        return aggregateCTData('HPA6Mo', 'HPA 6 Mo (%)', true);
      case 'amherstEstimatedAMI':
        return aggregateCTData('AmherstEstimatedAMI', 'AMI ($)');
      // case 'transactionPrice':
      //   return aggregateCTData('transactionPrice', 'Transaction Price ($)');
      // case 'transactionsCount':
      //   return aggregateCTData('transactionsCount', 'Transactions Count');
      case 'affordablePmt':
        return aggregateCTData('AffordabilityPmt', 'Affordable Pmt ($)');
      case 'affordabilityPrice':
        return aggregateCTData('AffordabilityPrice', 'Affordability Price ($)');
      case 'affordabilityScore':
        return aggregateCTData('AffordabilityScore', 'Affordability Score (%)', true);
      default:
        // console.log('No CT Data');
        return;
    }
  }

  const aggregateCTData = (property, text, reverse) => {
    let dataArr = [];   // This array is used to calculate min/max of the array
    let ctData = [];    // This array holds the ct name and ct value

    censusTractLayer.forEach(function (ct) {
      if (ct?.getProperty('ctData')) {
        dataArr.push(ct.getProperty('ctData')[property]);
        ctData.push({
          censusTract: ct.getProperty('ctData')['Geo'],
          property: ct.getProperty('ctData')[property]
        });
      }
      else {
        appNotify(`Missing census tract data for ${property}`, 'warning');
      }
    });

    let ctLowerRange, ctUpperRange;
    if (reverse) {    // Reverse to flip the lower/upper range
      ctLowerRange = Math.max(...dataArr);
      ctUpperRange = Math.min(...dataArr);
    } else {
      ctLowerRange = Math.min(...dataArr);
      ctUpperRange = Math.max(...dataArr);
    }
    let aggData = {
      property: ctData,
      text: text,
      min: ctLowerRange,
      max: ctUpperRange,
      format: ctFields.find(field => field.dataField === property)?.format
    }
    return aggData;
  }

  return null;
};

const mapStateToProps = (state, ownProps) => {
  const censusTracts = ownProps.module === Constants.Module.POSTACQ ? state.postAcqUWState.selectedOpportunityCensusTracts : state.propertyUWState.selectedOpportunityCensusTracts;
  return {
    censusTracts: censusTracts || []
  }
};

const mapDispatchToProps = {
  appNotify
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(CensusTractOverlay)
